/** Every WS7 write goes through useAction: one pending/error state, an announcement in the aria-live result line
 * for success and failure, and invalidation of the queries that show the changed data. */
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import { useAnnounce } from "../../components/announce";

export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const extra = e.details.length ? ` (${e.details.length} problem${e.details.length === 1 ? "" : "s"}: ${e.details.join("; ")})` : "";
    return `${e.status} ${e.message}${extra}`;
  }
  return e instanceof Error ? e.message : String(e);
}

export type ActionOpts<A extends unknown[], R> = {
  /** Announcement after success. */
  success: (result: R, ...args: A) => string;
  /** Prefix of the failure announcement ("Save account a1"). */
  label: string | ((...args: A) => string);
  /** Query keys (prefixes) to refresh after success. */
  invalidate?: QueryKey[];
};

export type Action<A extends unknown[], R> = {
  run: (...args: A) => Promise<R | undefined>;
  pending: boolean;
  error: unknown;
  clear: () => void;
};

export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>, opts: ActionOpts<A, R>): Action<A, R> {
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      const o = optsRef.current;
      setPending(true);
      setError(null);
      try {
        const r = await fnRef.current(...args);
        setPending(false);
        announce(o.success(r, ...args));
        await Promise.all((o.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
        return r;
      } catch (e) {
        setPending(false);
        setError(e);
        const label = typeof o.label === "function" ? o.label(...args) : o.label;
        announce(`${label} failed: ${describeError(e)}`, "error");
        return undefined;
      }
    },
    [announce, qc],
  );
  const clear = useCallback(() => setError(null), []);
  return { run, pending, error, clear };
}
